/**
 * Classification color palettes — raw color values for trade data categories.
 *
 * These are consumed by the data package's colorMaps to build domain-specific
 * ID → color Maps. Keeping raw values here centralizes all color definitions
 * in the tokens package.
 */

// ─── Region colors ──────────────────────────────────────────────────────────

export const regionColors = {
  asia: "rgb(108, 197, 134)",
  africa: "rgb(120, 59, 217)",
  oceania: "rgb(245, 190, 105)",
  europe: "rgb(88, 130, 184)",
  americas: "rgb(158, 70, 67)",
  other: "rgb(88, 103, 132)",
  servicesPartners: "#033095",
} as const;

// ─── HS sector colors (shared by HS92, HS12, HS22) ─────────────────────────

export const hsSectorColors = {
  vegetable: "#F5CF23",
  minerals: "rgb(187, 150, 138)",
  chemicals: "rgb(197, 123, 217)",
  textiles: "rgb(125, 218, 161)",
  stone: "rgb(218, 180, 125)",
  metals: "rgb(217, 123, 123)",
  machinery: "rgb(123, 162, 217)",
  electronics: "rgb(125, 218, 218)",
  transport: "rgb(141, 123, 216)",
  others: "#2a607c",
  services: "rgb(178, 61, 109)",
} as const;

// ─── SITC sector colors ─────────────────────────────────────────────────────

export const sitcSectorColors = {
  food: "#F5CF23",
  drinks: "#FE85AD",
  materials: "rgb(217, 123, 123)",
  mineral: "rgb(187, 150, 138)",
  fats: "#FDA81B",
  chemical: "rgb(197, 123, 217)",
  goods: "#DA3329",
  machinery: "rgb(123, 162, 217)",
  commodities: "#00A6AA",
  miscellaneous: "#2a607c",
  services: "rgb(178, 61, 109)",
} as const;

// ─── Cluster colors (Product Space) ─────────────────────────────────────────

export const clusterColors = {
  agriculturalGoods: "#EAC218",
  constructionGoods: "#D1852A",
  electronics: "#52E2DE",
  chemicalsAndBasicMetals: "#A42DE2",
  metalworkingAndMachinery: "#C64646",
  minerals: "#7C6760",
  textileAndHomeGoods: "#757777",
  apparel: "#36B250",
} as const;

// ─── Green scale colors (RCA-based) ─────────────────────────────────────────

export const greenScaleColors = {
  greenProductHighRca: "#2DB464",
  notGreenProductHighRca: "#FBB895",
  greenProductLowRca: "#C6E4C9",
  notGreenProductLowRca: "#E9E9E9",
} as const;

// ─── ID → color maps ────────────────────────────────────────────────────────
// These map domain IDs (region groups, product sections, clusters) to their
// palette colors. The tokens package is the single source of truth for all
// visual styling: raw palettes AND the ID → color mappings.

// -- Region ID → color --

export const NewRegionColor = {
  Asia: regionColors.asia,
  Africa: regionColors.africa,
  Oceania: regionColors.oceania,
  Europe: regionColors.europe,
  Americas: regionColors.americas,
  Other: regionColors.other,
  ServicesPartners: regionColors.servicesPartners,
} as const;
export type NewRegionColor = (typeof NewRegionColor)[keyof typeof NewRegionColor];

export const groupedRegionColorMap: Map<string, string> = new Map([
  ["group-2", NewRegionColor.Africa],
  ["group-19", NewRegionColor.Americas],
  ["group-142", NewRegionColor.Asia],
  ["group-150", NewRegionColor.Europe],
  ["group-9", NewRegionColor.Oceania],
  ["group-998", NewRegionColor.Other],
  ["group-994", NewRegionColor.ServicesPartners],
]);

// The following is also the order of regions to be used when sorting data, e.g., in stack chart
export const groupNames: Map<string, string> = new Map([
  ["group-2", "Africa"],
  ["group-19", "Americas"],
  ["group-142", "Asia"],
  ["group-150", "Europe"],
  ["group-9", "Oceania"],
  ["group-998", "Other"],
  ["group-994", "Services Partners"],
]);

// -- HS92 product section ID → color --

const HS92Color = {
  vegetable: hsSectorColors.vegetable,
  minerals: hsSectorColors.minerals,
  chemicals: hsSectorColors.chemicals,
  textiles: hsSectorColors.textiles,
  stone: hsSectorColors.stone,
  metals: hsSectorColors.metals,
  machinery: hsSectorColors.machinery,
  electronics: hsSectorColors.electronics,
  transport: hsSectorColors.transport,
  others: hsSectorColors.others,
  services: hsSectorColors.services,
} as const;

export const hs92ColorsMap: Map<string, string> = new Map([
  ["product-HS92-1", HS92Color.textiles],
  ["product-HS92-2", HS92Color.vegetable],
  ["product-HS92-3", HS92Color.stone],
  ["product-HS92-4", HS92Color.minerals],
  ["product-HS92-5", HS92Color.metals],
  ["product-HS92-6", HS92Color.chemicals],
  ["product-HS92-7", HS92Color.transport],
  ["product-HS92-8", HS92Color.machinery],
  ["product-HS92-9", HS92Color.electronics],
  ["product-HS92-10", HS92Color.others],
  ["product-HS92-14", HS92Color.services],
]);

// -- HS12 product section ID → color --

export const hs12ColorsMap: Map<string, string> = new Map([
  ["product-HS12-1", HS92Color.textiles],
  ["product-HS12-2", HS92Color.vegetable],
  ["product-HS12-3", HS92Color.stone],
  ["product-HS12-4", HS92Color.minerals],
  ["product-HS12-5", HS92Color.metals],
  ["product-HS12-6", HS92Color.chemicals],
  ["product-HS12-7", HS92Color.transport],
  ["product-HS12-8", HS92Color.machinery],
  ["product-HS12-9", HS92Color.electronics],
  ["product-HS12-10", HS92Color.others],
  ["product-HS12-14", HS92Color.services],
]);

// -- HS22 product section ID → color --

export const hs22ColorsMap: Map<string, string> = new Map([
  ["product-HS22-1", HS92Color.textiles],
  ["product-HS22-2", HS92Color.vegetable],
  ["product-HS22-3", HS92Color.stone],
  ["product-HS22-4", HS92Color.minerals],
  ["product-HS22-5", HS92Color.metals],
  ["product-HS22-6", HS92Color.chemicals],
  ["product-HS22-7", HS92Color.transport],
  ["product-HS22-8", HS92Color.machinery],
  ["product-HS22-9", HS92Color.electronics],
  ["product-HS22-10", HS92Color.others],
  ["product-HS22-14", HS92Color.services],
]);

// -- SITC product section ID → color --

const SITCColor = {
  food: sitcSectorColors.food,
  drinks: sitcSectorColors.drinks,
  materials: sitcSectorColors.materials,
  mineral: sitcSectorColors.mineral,
  fats: sitcSectorColors.fats,
  chemical: sitcSectorColors.chemical,
  goods: sitcSectorColors.goods,
  machinery: sitcSectorColors.machinery,
  commodities: sitcSectorColors.commodities,
  miscellaneous: sitcSectorColors.miscellaneous,
  services: sitcSectorColors.services,
} as const;

export const sitcColorsMap: Map<string, string> = new Map([
  ["product-SITC-1", SITCColor.food],
  ["product-SITC-2", SITCColor.drinks],
  ["product-SITC-3", SITCColor.materials],
  ["product-SITC-4", SITCColor.mineral],
  ["product-SITC-5", SITCColor.fats],
  ["product-SITC-6", SITCColor.chemical],
  ["product-SITC-7", SITCColor.goods],
  ["product-SITC-8", SITCColor.machinery],
  ["product-SITC-9", SITCColor.commodities],
  ["product-SITC-10", SITCColor.miscellaneous],
  ["product-SITC-14", SITCColor.services],
]);

// -- Cluster name → color (Product Space) --

export const clusterColorsMap: Map<string, string> = new Map([
  ["Agricultural Goods", clusterColors.agriculturalGoods],
  ["Construction Goods", clusterColors.constructionGoods],
  ["Electronics", clusterColors.electronics],
  ["Chemicals and Basic Metals", clusterColors.chemicalsAndBasicMetals],
  ["Metalworking and Machinery", clusterColors.metalworkingAndMachinery],
  ["Minerals", clusterColors.minerals],
  ["Textile and Home Goods", clusterColors.textileAndHomeGoods],
  ["Apparel", clusterColors.apparel],
]);

// -- Green product constants (RCA-based) --

export const GREEN_PRODUCT_HIGH_RCA = greenScaleColors.greenProductHighRca;
export const NOT_GREEN_PRODUCT_HIGH_RCA = greenScaleColors.notGreenProductHighRca;
export const GREEN_PRODUCT_LOW_RCA = greenScaleColors.greenProductLowRca;
export const NOT_GREEN_PRODUCT_LOW_RCA = greenScaleColors.notGreenProductLowRca;

// -- Generic product color map lookup --

const productColorMapsByKey: Record<string, Map<string, string>> = {
  HS92: hs92ColorsMap,
  HS12: hs12ColorsMap,
  HS22: hs22ColorsMap,
  SITC: sitcColorsMap,
};

export function getProductColorMap(classKey: string): Map<string, string> | undefined {
  return productColorMapsByKey[classKey];
}
